import { Component, Input, inject, signal, ViewChild } from '@angular/core';

import { HttpHeaders } from '@angular/common/http';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { finalize } from 'rxjs';

import { APIService } from '@core/services';
import { PatientService } from '@core/services/patient.service';
import { HTTPMethods, ModalTypes } from '@core/enums';

import { ButtonComponent, ButtonOptions, ModalComponent, ModalOptions } from '@shared/components';

@Component({
  selector: 'ncp-eprescription-actions',
  standalone: true,
  imports: [ButtonComponent, ModalComponent],
  templateUrl: './eprescription-actions.component.html',
  styleUrl: './eprescription-actions.component.scss',
})
export class EprescriptionActionsComponent {
  @Input() data!: any;
  @Input() reloadData!: () => void;
  @Input() tbl!: any;

  @ViewChild('deleteMod') protected deleteMod!: ModalComponent;
  @ViewChild('previewMod') protected previewMod!: ModalComponent;

  @ViewChild('delBtn') protected delBtn!: ButtonComponent;
  @ViewChild('printBtn') protected printBtn!: ButtonComponent;
  @ViewChild('cdaBtn') protected cdaBtn!: ButtonComponent;

  private readonly apiService = inject(APIService);
  private readonly patientService = inject(PatientService);
  private readonly sanitizer = inject(DomSanitizer);

  protected previewUrl = signal<SafeResourceUrl | null>(null);
  protected previewType = signal<string>('application/pdf');

  private objectUrl: string | null = null;

  protected deleteBtnOptions = signal<ButtonOptions>(
    new ButtonOptions({
      tooltip: 'delete_entry',
      class: 'btn-danger',
      icon: 'bi bi-trash3 fs-5',
      onClick: () => {
        this.delBtn.blur();
        this.deleteMod.open();
      },
    })
  );

  protected printBtnOptions = signal<ButtonOptions>(
    new ButtonOptions({
      tooltip: 'preview_pdf',
      class: 'btn-primary',
      isLoading: false,
      icon: 'bi bi-file-earmark-pdf fs-5',
      onClick: () => {
        this.print();
      },
    })
  );

  protected cdaBtnOptions = signal<ButtonOptions>(
    new ButtonOptions({
      tooltip: 'preview_cda',
      class: 'btn-secondary',
      isLoading: false,
      icon: 'bi bi-file-earmark-spreadsheet fs-5',
      onClick: () => {
        this.cda();
      },
    })
  );

  protected deleteModOptions = signal<ModalOptions>(
    new ModalOptions({
      id: 'deleteEpModal',
      title: 'delete_entry',
      message: 'confirm_delete',
      onYes: () => {
        this.delete();
      },
    })
  );

  protected previewModOptions = signal<ModalOptions>(
    new ModalOptions({
      id: 'previewEpModal',
      title: 'preview_document',
      type: ModalTypes.empty,
      options: {
        animation: true,
        fullscreen: true,
        backdrop: 'static',
        keyboard: false,
      },
    })
  );

  private get dob(): string {
    const patientRaw = sessionStorage.getItem('patient') || '{}';
    const patient = JSON.parse(patientRaw);

    return patient?.data?.dob || patient?.data?.date_of_birth || patient?.data?.birthDate || '';
  }

  private get dobDateOnly(): string {
    return this.dob?.split('T')[0] || this.dob;
  }

  private get documentId(): string {
    if (this.data.document_id) {
      return this.data.document_id;
    }

    const issueDate = String(this.data.date_of_issue || '').replaceAll('-', '');

    const prescriptionId = String(this.data.prescription_id || '').padStart(10, '0');

    return `ePid.CY${issueDate}${prescriptionId}.cda`;
  }

  private get headers(): HttpHeaders {
    return new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('jwt', this.patientService.patient?.token || '')
      .set('pToken', this.patientService.pToken || '');
  }

  protected delete(): void {
    this.apiService
      .call({
        path: '/delete-prescription',
        headers: this.headers,
        type: HTTPMethods.PUT,
        body: this.data.original_row || this.data,
      })
      ?.subscribe({
        next: (res: any) => {
          if (res?.status === 1) {
            this.tbl?.options?.()?.reloadData?.();
            this.deleteMod.close();
          }
        },
        error: (error) => {
          console.error('Error deleting prescription:', error);
        },
      });
  }

  protected print(): void {
    this.printBtnOptions.update((options) => ({
      ...options,
      isLoading: true,
    }));

    this.apiService
      .call({
        path: '/get-ep_pdf',
        headers: this.headers,
        type: HTTPMethods.POST,
        body: {
          eP_id: String(this.data.prescription_id),
          dob: this.dob,
        },
      })
      ?.pipe(
        finalize(() => {
          this.printBtnOptions.update((options) => ({
            ...options,
            isLoading: false,
          }));
        })
      )
      .subscribe({
        next: (res: any) => {
          const base64 = res?.srv_results?.data;

          if (res?.status === 1 && res?.srv_results?.status === 1 && base64) {
            this.previewPdf(base64);

            this.printBtn.blur();
            this.previewMod.open();

            return;
          }

          console.error('Unable to load prescription PDF:', res);
        },
        error: (error) => {
          console.error('Error loading prescription PDF:', error);
        },
      });
  }

  protected cda(): void {
    this.cdaBtnOptions.update((options) => ({
      ...options,
      isLoading: true,
    }));

    this.apiService
      .call({
        path: '/get-cda',
        headers: this.headers,
        type: HTTPMethods.POST,
        body: {
          dob: this.dobDateOnly,
          document_id: this.documentId,
        },
      })
      ?.pipe(
        finalize(() => {
          this.cdaBtnOptions.update((options) => ({
            ...options,
            isLoading: false,
          }));
        })
      )
      .subscribe({
        next: (res: any) => {
          const base64 = res?.srv_results?.data;

          if (res?.status === 1 && res?.srv_results?.status === 1 && base64) {
            this.previewCda(base64);

            this.cdaBtn.blur();
            this.previewMod.open();

            return;
          }

          console.error('Unable to load CDA:', res);
        },
        error: (error) => {
          console.error('Error loading CDA:', error);
        },
      });
  }

  protected previewPdf(base64: string): void {
    if (!base64) {
      return;
    }

    try {
      const cleanBase64 = base64.includes(',') ? base64.split(',')[1] : base64;

      const byteChars = atob(cleanBase64);
      const byteNumbers = new Array(byteChars.length);

      for (let i = 0; i < byteChars.length; i++) {
        byteNumbers[i] = byteChars.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);

      const blob = new Blob([byteArray], {
        type: 'application/pdf',
      });

      /*
       * Remove previous object URL if one exists.
       */
      if (this.objectUrl) {
        URL.revokeObjectURL(this.objectUrl);
      }

      this.objectUrl = URL.createObjectURL(blob);

      this.previewUrl.set(this.sanitizer.bypassSecurityTrustResourceUrl(this.objectUrl));

      this.previewType.set('application/pdf');
    } catch (error) {
      console.error('Unable to preview PDF:', error);
    }
  }

  protected previewCda(base64: string): void {
    if (!base64) {
      return;
    }

    try {
      /*
       * CDA does not use the PDF object URL,
       * so clean it if one exists.
       */
      if (this.objectUrl) {
        URL.revokeObjectURL(this.objectUrl);
        this.objectUrl = null;
      }

      const cleanBase64 = base64.includes(',') ? base64.split(',')[1] : base64;

      const dataUrl = `data:text/html;base64,${cleanBase64}`;

      this.previewUrl.set(this.sanitizer.bypassSecurityTrustResourceUrl(dataUrl));

      this.previewType.set('text/html');
    } catch (error) {
      console.error('Unable to preview CDA:', error);
    }
  }
}
